import { PrintReceiptRepositoryInterface } from "@/data/persistences/repositories/contracts/PrintReceiptRepositoryInterface";
import ApiService from "@/app/infrastructures/services/ApiService";
import { PrintReceiptEndpoint } from "../../endpoints/PrintReceiptEndpoint";
import { PrintReceiptMapper } from "@/data/persistences/mappers/PrintReceiptMapper";
import { ListInvoiceApiRequest } from "@/data/payload/api/PrintReceiptRequest";
import { PrintReceiptListInvoice } from "@/domain/entities/PrintReceipt";
import {
  CreateInvoiceApiRequest,
  CreateInvoiceDetailBillApiRequest,
  ListBookingHistoryDetailApiRequest,
  SettlementInvoiceRequest,
  DraftPreviewForUpdateRequest,
  UpdateInvoiceRequest,
  ActivityRequest
} from "@/data/payload/api/PrintReceiptApiRequest";
import { AddDraftReceiptApiRequest } from "@/data/payload/api/AddDraftReceiptApiRequest";

export class PrintReceiptApiRepository
  implements PrintReceiptRepositoryInterface {
  private service: ApiService;
  private mapper: PrintReceiptMapper;
  private endpoints: PrintReceiptEndpoint;

  constructor(
    service: ApiService,
    mapper: PrintReceiptMapper,
    endpoints: PrintReceiptEndpoint
  ) {
    this.service = service;
    this.mapper = mapper;
    this.endpoints = endpoints;
  }

  public async create(payload: CreateInvoiceApiRequest) {
    const resp = await this.service.invoke(
      "POST",
      this.endpoints.create(),
      {},
      payload
    );
    return this.mapper.convertCreateInvoiceData(resp);
  }

  public async createInvoice(payload: CreateInvoiceDetailBillApiRequest) {
    const resp = await this.service.invoke(
      "POST",
      this.endpoints.create(),
      {},
      payload
    );
    return this.mapper.convertCreateInvoiceData(resp);
  }

  public async getDetailInvoice(id: number) {
    const resp = await this.service.invoke(
      "GET",
      this.endpoints.getDetailInvoice(id),
      {}
    );
    return this.mapper.convertDetailInvoiceData(resp);
  }

  public async getBookingHistoryDetail(
    payload: ListBookingHistoryDetailApiRequest
  ) {
    const resp = await this.service.invoke(
      "POST",
      this.endpoints.getBookingHistoryDetail(),
      {},
      payload
    );
    return this.mapper.convertBookingHistoryDetailDataFromApi(resp);
  }

  public async sendInvoice({
    id,
    dataRequest
  }: {
    id: number;
    dataRequest: FormData;
  }): Promise<any> {
    const resp = await this.service.invokePostWithFormData(
      "post",
      this.endpoints.sendInvoice(id),
      {},
      {
        toFormData() {
          return dataRequest;
        }
      }
    );
    return this.mapper.convertInvoiceFromApi(resp);
  }

  public async getListInvoices(
    params: ListInvoiceApiRequest
  ): Promise<PrintReceiptListInvoice> {
    const resp = await this.service.invoke(
      "get",
      this.endpoints.getListInvoices(params),
      {}
    );
    return this.mapper.convertListInvoices(resp);
  }

  public async settlementInvoice(
    id: number,
    payload: SettlementInvoiceRequest
  ): Promise<any> {
    const resp = await this.service.invoke(
      "POST",
      this.endpoints.settlementInvoice(id),
      undefined,
      payload
    );
    return resp;
  }

  public async paymentDetailInvoice(id: number): Promise<any> {
    const resp = await this.service.invoke(
      "get",
      this.endpoints.getDetailInvoice(id),
      {}
    );
    return resp;
  }

  public async addDraft(payload: AddDraftReceiptApiRequest): Promise<any> {
    const resp = await this.service.invoke(
      "POST",
      this.endpoints.addDraft(),
      undefined,
      payload
    );
    return this.mapper.convertDraftFromApi(resp);
  }

  public async getPreview(): Promise<any> {
    const resp = await this.service.invoke(
        "get",
        this.endpoints.getPreview()
    );
    return this.mapper.convertPreviewFromApi(resp);
  }

  public async getInvoiceForUpdate(id: number) {
    const resp = await this.service.invoke(
      "GET",
      this.endpoints.getInvoiceForUpdate(id),
      {}
    );
    return this.mapper.convertDetailInvoiceForUpdateFromApi(resp);
  }

  public async draftPreviewForUpdate(
    id: number,
    payload: DraftPreviewForUpdateRequest
  ) {
    const resp = await this.service.invoke(
      "POST",
      this.endpoints.draftPreviewForUpdate(id),
      undefined,
      payload
    );
    return this.mapper.convertDraftPreviewForUpdateFromApi(resp);
  }

  public async updateInvoice(id: number, payload: UpdateInvoiceRequest) {
    const resp = await this.service.invoke(
      "PUT",
      this.endpoints.updateInvoice(id),
      undefined,
      payload
    );
    return this.mapper.convertUpdateReceiptFromApi(resp);
  }

  public async activity(id: number, payload: ActivityRequest) {
    await this.service.invoke(
      "POST",
      this.endpoints.activity(id),
      undefined,
      payload
    );
    return true;
  }

  public async clearDraftItems() {
    await this.service.invoke("DELETE", this.endpoints.clearDraftItems());
    return true;
  }
}
